import React from "react";
import { StagedFile } from "./definitions";
import {
  UploadUrlRequest,
  UploadUrlResponse,
} from "components/documents-table-os/definitions";
import {
  BatchRegisterDocumentsInput,
  BatchUpdateDocumentsInput,
  BatchDeleteDocumentsInput,
} from "components/documents-table-os/definitions";
import {
  getFileExtension,
  getFileNameWithoutExtension,
  getFileSize,
} from "utils/date-format";
import { getAllVersionsForFile } from "./version-utils";
import { GaiaConstants } from "utils";

/**
 * We "Remove" new files not yet uploaded to S3
 * We "Delete" files already previously uploaded to S3
 */
export const getWizardDeleteAction = (
  files: StagedFile[],
  pastTense?: boolean,
): string => {
  if (files.at(0) === undefined) return "Delete";
  const baseAction = files.at(0)!.isNew ? "Remove" : "Delete";
  return pastTense ? `${baseAction}d` : baseAction;
};

export const getWizardDeleteInfo = (
  files: StagedFile[],
  suffix: string,
  pastTense?: boolean,
): string => {
  const action = getWizardDeleteAction(files, pastTense);
  const linkNum = files.filter((f) => f.link).length;
  const fileNum = files.filter((f) => f.file || f.originalDocument).length;

  if (linkNum > 0 && fileNum > 0) {
    return `${action} ${linkNum === 1 ? "link" : "links"} and ${
      fileNum === 1 ? "file" : "files"
    }${suffix}`;
  }
  if (linkNum > 0) {
    return `${action} ${linkNum === 1 ? "link" : "links"}${suffix}`;
  }
  if (fileNum > 0) {
    return `${action} ${fileNum === 1 ? "file" : "files"}${suffix}`;
  }

  return `${action} items${suffix}`;
};

/**
 * Creates a delete confirmation message that includes version information
 * when the file has multiple versions
 */
export const getWizardVersionedDeleteMessage = (
  files: StagedFile[],
  fileNames: string,
): React.ReactNode => {
  const hasMultipleVersions = files.some((file) => {
    if (!file.isNew && file.originalDocument) {
      const versions = file.originalDocument?.versions;
      return versions && versions.length > 0;
    }
    return false;
  });
  const action = getWizardDeleteAction(files);

  if (hasMultipleVersions) {
    return (
      <>
        {action} <b>all versions</b> of {fileNames}?
      </>
    );
  }

  return (
    <>
      {action} {fileNames}?
    </>
  );
};

/**
 * Helper function to process new file uploads
 */
export const processNewFileUploads = async (
  stagedFiles: StagedFile[],
  actualParentId: string,
  effectiveGaiaObjectId: string,
  category: string,
  teamName: string,
  uploadUrlCall: any,
  registerDocCall: any,
): Promise<{ uploaded: number; errors: number }> => {
  const results = { uploaded: 0, errors: 0 };
  const newFiles = stagedFiles.filter((f) => f.isNew && !f.toDelete);

  for (const stagedFile of newFiles) {
    try {
      let uploadUrlResponse: UploadUrlResponse | null = null;

      if (stagedFile.file) {
        const request: UploadUrlRequest = {
          prefix: `${category}/${actualParentId}`,
          fileName: stagedFile.file.name,
          teamName: teamName,
          securityLevel: "Normal",
        };
        uploadUrlResponse = await uploadUrlCall(request).unwrap();

        if (uploadUrlResponse) {
          await fetch(uploadUrlResponse.url, {
            method: "PUT",
            body: stagedFile.file,
            headers: {
              "x-amz-expected-bucket-owner": uploadUrlResponse.bucketOwner,
            },
          });
        }
      }

      const s3Key = uploadUrlResponse
        ? `${category}/${actualParentId}/${uploadUrlResponse.uniqueKey}/${
            stagedFile.file!.name
          }`
        : "";

      const registerRequest: BatchRegisterDocumentsInput = {
        documents: [
          {
            parentId: actualParentId,
            teamName: teamName,
            fileName: stagedFile.fileName,
            documentType: stagedFile.documentType,
            securityLevel: "Normal",
            s3Key: s3Key,
            externalUrl: stagedFile.link || "",
            extensionType: stagedFile.file
              ? getFileExtension(stagedFile.file.name)
              : GaiaConstants.LINK_EXTENSION_TYPE,
            gaiaObjectId: effectiveGaiaObjectId,
            ...(stagedFile.file && {
              size: getFileSize(stagedFile.file.size),
            }),
          },
        ],
      };

      await registerDocCall(registerRequest).unwrap();
      results.uploaded++;
    } catch (e) {
      console.error("Failed to upload file:", stagedFile.fileName, e);
      results.errors++;
    }
  }

  return results;
};

/**
 * Helper function to process pending versions
 */
export const processPendingVersions = async (
  stagedFiles: StagedFile[],
  actualParentId: string,
  effectiveGaiaObjectId: string,
  category: string,
  teamName: string,
  uploadUrlCall: any,
  registerDocCall: any,
): Promise<{ uploaded: number; errors: number }> => {
  const results = { uploaded: 0, errors: 0 };
  const filesWithPendingVersions = stagedFiles.filter(
    (f) => f.pendingVersions && f.pendingVersions.length > 0 && !f.toDelete,
  );

  for (const stagedFile of filesWithPendingVersions) {
    if (!stagedFile.pendingVersions) continue;

    for (const pendingVersion of stagedFile.pendingVersions) {
      try {
        const request: UploadUrlRequest = {
          prefix: `${category}/${actualParentId}`,
          fileName: pendingVersion.file.name,
          teamName: teamName,
          securityLevel: "Normal",
        };
        const uploadUrlResponse = await uploadUrlCall(request).unwrap();

        if (uploadUrlResponse) {
          await fetch(uploadUrlResponse.url, {
            method: "PUT",
            body: pendingVersion.file,
            headers: {
              "x-amz-expected-bucket-owner": uploadUrlResponse.bucketOwner,
            },
          });
        }

        const s3Key = `${category}/${actualParentId}/${uploadUrlResponse.uniqueKey}/${pendingVersion.file.name}`;

        const registerRequest: BatchRegisterDocumentsInput = {
          documents: [
            {
              parentId: actualParentId,
              teamName: teamName,
              fileName: pendingVersion.fileName,
              documentType: pendingVersion.documentType,
              securityLevel: "Normal",
              s3Key: s3Key,
              externalUrl: "",
              extensionType: getFileExtension(pendingVersion.file.name),
              gaiaObjectId: effectiveGaiaObjectId,
              size: getFileSize(pendingVersion.file.size),
            },
          ],
        };

        await registerDocCall(registerRequest).unwrap();
        results.uploaded++;
      } catch (e) {
        console.error(
          "Failed to upload pending version:",
          pendingVersion.fileName,
          e,
        );
        results.errors++;
      }
    }
  }

  return results;
};

/**
 * Helper function to process renamed files
 */
export const processRenamedFiles = async (
  stagedFiles: StagedFile[],
  updateDocCall: any,
): Promise<{ updated: number; errors: number }> => {
  const results = { updated: 0, errors: 0 };
  const renamedFiles = stagedFiles.filter(
    (f) =>
      !f.isNew &&
      !f.toDelete &&
      f.originalDocument &&
      f.fileName !== getFileNameWithoutExtension(f.originalDocument.fileName),
  );

  for (const stagedFile of renamedFiles) {
    try {
      if (stagedFile.originalDocument) {
        const allVersions = getAllVersionsForFile(stagedFile);

        const updateRequest: BatchUpdateDocumentsInput = {
          updates: allVersions.map((v) => ({
            documentId: v.documentId,
            fileName: stagedFile.fileName,
            documentType: stagedFile.documentType,
          })),
        };
        await updateDocCall(updateRequest).unwrap();
        results.updated += allVersions.length;
      }
    } catch (e) {
      console.error("Failed to update file:", stagedFile.fileName, e);
      results.errors++;
    }
  }

  return results;
};

/**
 * Helper function to process file deletions
 */
export const processFileDeletions = async (
  stagedFiles: StagedFile[],
  deleteDocCall: any,
): Promise<{ deleted: number; errors: number }> => {
  const results = { deleted: 0, errors: 0 };
  const filesToDelete = stagedFiles.filter((f) => !f.isNew && f.toDelete);

  const deletions = filesToDelete
    .filter((f) => f.originalDocument)
    .map((f) => ({
      documentId: f.originalDocument!.documentId,
      allVersions: true,
    }));

  if (deletions.length > 0) {
    try {
      const request: BatchDeleteDocumentsInput = {
        deletions: deletions,
      };
      const response = await deleteDocCall(request).unwrap();
      results.deleted += response.succeededDocuments?.length || 0;
      results.errors += response.failedDocuments?.length || 0;
    } catch (e) {
      console.error("Failed to delete files:", e);
      results.errors += deletions.length;
    }
  }

  return results;
};
